import { MouseButton } from './enums';
import { ToolGroup } from './tool-group';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration, IServiceManager } from '../core/types';
import type { IViewport } from '../viewports/types';
import { ViewportType } from '../viewports/types';
import { is3DViewport, isCprViewport, isSliceViewport } from '../viewports/viewport-categories';
import { ContrastTool } from './appearance/contrast-tool';
import { CarmRotateTool } from './camera/carm-rotate-tool';
import { PanTool } from './camera/pan-tool';
import { RotateTool } from './camera/rotate-tool';
import { VerticalRotateTool } from './camera/vertical-rotate-tool';
import { ZoomTool } from './camera/zoom-tool';
import { DistanceMeasurementTool } from './measurement/distance-tool';
import { CurrentLocationTool } from './selection/current-location-tool';
import { DiameterEditTool } from './sizing/diameter-edit-tool';
import { AngleScrollTool } from './slice/angle-scroll-tool';
import { SliceScrollTool } from './slice/slice-scroll-tool';

export interface PrimaryToolChangedPayload {
    viewportId: string;
    /** Tool now owning the Primary mouse button. */
    toolId: string;
}

/**
 * Service managing ToolGroups and viewport-to-group assignments.
 *
 * Provides:
 * - ToolGroup creation and destruction
 * - Viewport → ToolGroup mapping
 * - Default ToolGroup configurations per viewport type
 */
export class ToolGroupManager extends PubSubService {
    static readonly SERVICE_NAME = 'ToolGroupManager';

    static readonly REGISTRATION: ServiceRegistration = {
        name: ToolGroupManager.SERVICE_NAME,
        factory: (context: ServiceContext) => new ToolGroupManager(context),
    };

    readonly EVENTS = {
        TOOL_GROUP_CREATED: 'toolgroup:created',
        TOOL_GROUP_DESTROYED: 'toolgroup:destroyed',
        /** Emitted whenever the Primary mouse button binding of a viewport changes. */
        PRIMARY_TOOL_CHANGED: 'toolgroup:primary:changed',
    } as const;

    private toolGroups = new Map<string, ToolGroup>();
    private viewportToGroupId = new Map<string, string>();
    private readonly services: IServiceManager;

    static readonly CAMERA_TOOL_IDS = [
        RotateTool.toolName,
        PanTool.toolName,
        ZoomTool.toolName,
        ContrastTool.toolName,
    ] as const;

    /**
     * Tools that may not share the Primary button with each other. Camera
     * tools plus CarmRotateTool (bound during C-arm edit mode) — unlike
     * CurrentLocation, they all consume drags, so two of them on Primary would
     * fight over the same gesture.
     */
    static readonly PRIMARY_EXCLUSIVE_TOOL_IDS = [
        ...ToolGroupManager.CAMERA_TOOL_IDS,
        CarmRotateTool.toolName,
    ] as const;

    constructor(context: ServiceContext) {
        super();
        this.services = context.services;
    }

    // ── ToolGroup Lifecycle ──

    createToolGroup(id: string): ToolGroup {
        if (this.toolGroups.has(id)) {
            console.warn(`[ToolGroupManager] Group '${id}' already exists, returning existing`);
            return this.toolGroups.get(id)!;
        }

        const group = new ToolGroup(id, this.services);
        this.toolGroups.set(id, group);
        this.broadcast(this.EVENTS.TOOL_GROUP_CREATED, { groupId: id });
        return group;
    }

    destroyToolGroup(id: string): void {
        const group = this.toolGroups.get(id);
        if (!group) return;

        // Remove all viewport mappings for this group
        for (const [vpId, gId] of this.viewportToGroupId) {
            if (gId === id) {
                this.viewportToGroupId.delete(vpId);
            }
        }

        group.dispose();
        this.toolGroups.delete(id);
        this.broadcast(this.EVENTS.TOOL_GROUP_DESTROYED, { groupId: id });
    }

    getToolGroup(id: string): ToolGroup | null {
        return this.toolGroups.get(id) ?? null;
    }

    getAllToolGroups(): ToolGroup[] {
        return Array.from(this.toolGroups.values());
    }

    // ── Viewport Mapping ──

    addViewportToToolGroup(viewportId: string, toolGroupId: string): void {
        const group = this.toolGroups.get(toolGroupId);
        if (!group) {
            console.error(`[ToolGroupManager] Cannot add viewport to non-existent group '${toolGroupId}'`);
            return;
        }

        // Remove from previous group if any
        this.removeViewportFromToolGroup(viewportId);

        this.viewportToGroupId.set(viewportId, toolGroupId);
    }

    removeViewportFromToolGroup(viewportId: string): void {
        const previousGroupId = this.viewportToGroupId.get(viewportId);
        if (previousGroupId) {
            const previousGroup = this.toolGroups.get(previousGroupId);
            previousGroup?.removeViewport(viewportId);
            this.viewportToGroupId.delete(viewportId);
        }
    }

    getToolGroupForViewport(viewportId: string): ToolGroup | null {
        const groupId = this.viewportToGroupId.get(viewportId);
        if (!groupId) return null;
        return this.toolGroups.get(groupId) ?? null;
    }

    /**
     * Switch the Primary mouse button binding on a single viewport's ToolGroup.
     * Removes Primary from other camera tools and adds it to the given tool.
     *
     * @returns true if the binding was applied, false if the viewport has no ToolGroup.
     */
    setPrimaryToolForViewport(viewportId: string, toolId: string): boolean {
        const toolGroup = this.getToolGroupForViewport(viewportId);
        if (!toolGroup) return false;

        // Vérifie que le tool existe dans le group
        const toolExists = toolGroup.hasTool(toolId);
        if (toolExists) {
            this.applyPrimaryBinding(toolGroup, toolId);
            this.broadcast(this.EVENTS.PRIMARY_TOOL_CHANGED, {
                viewportId,
                toolId,
            } satisfies PrimaryToolChangedPayload);
            return true;
        }
        return false;
    }

    /**
     * Tool from PRIMARY_EXCLUSIVE_TOOL_IDS currently owning the Primary mouse
     * button on a viewport's ToolGroup, or null if none (or no group).
     *
     * Ignores non-exclusive Primary tools (e.g. CurrentLocationTool, which is
     * always bound) and does not depend on tool insertion order — CarmRotateTool
     * joins the group only when a C-arm edit starts, after every default tool.
     */
    getPrimaryExclusiveToolForViewport(viewportId: string): string | null {
        const toolGroup = this.getToolGroupForViewport(viewportId);
        if (!toolGroup) return null;

        const exclusiveIds: readonly string[] = ToolGroupManager.PRIMARY_EXCLUSIVE_TOOL_IDS;
        return (
            toolGroup.getToolsForBinding(MouseButton.PRIMARY).find((tool) => exclusiveIds.includes(tool.id))
                ?.id ?? null
        );
    }

    private applyPrimaryBinding(group: ToolGroup, toolId: string): void {
        const primaryBinding = { mouseButton: MouseButton.PRIMARY };

        // Strip the primary binding from the other exclusive tools
        for (const id of ToolGroupManager.PRIMARY_EXCLUSIVE_TOOL_IDS) {
            if (id === toolId) continue;
            group.removeToolBinding(id, primaryBinding);
        }
        // Ajoute le binding primaire sur le tool choisi
        group.addToolBinding(toolId, primaryBinding);
    }

    // ── Default ToolGroup Configurations ──

    /**
     * Create a ToolGroup with default bindings for a given viewport type.
     * If a group with the computed ID already exists, returns the existing one.
     */
    createDefaultToolGroup(viewportType: ViewportType, viewport: IViewport): ToolGroup {
        const groupId = `default-${viewportType}-${viewport.id}`;

        let group = this.toolGroups.get(groupId);
        if (!group) {
            group = this.createToolGroup(groupId);
            this.configureDefaultBindings(group, viewportType);
        }

        group.addViewport(viewport);
        this.viewportToGroupId.set(viewport.id, groupId);

        return group;
    }

    private configureDefaultBindings(group: ToolGroup, viewportType: ViewportType): void {
        const is3D = is3DViewport(viewportType);
        const isSlice = isSliceViewport(viewportType);
        const isCPR = isCprViewport(viewportType);

        if (is3D) {
            // 3D Volume viewport
            group.addTool(RotateTool.toolName);
            group.addTool(ZoomTool.toolName);
            group.addTool(PanTool.toolName);
            group.addTool(VerticalRotateTool.toolName);
            group.addTool(CurrentLocationTool.toolName);
            group.addTool(DistanceMeasurementTool.toolName);
            // No initial binding: Rotate keeps Primary until the toolbar hands
            // the button over (on 2D, Contrast holds it from the start)
            group.addTool(ContrastTool.toolName);

            group.setToolActive(RotateTool.toolName, {
                bindings: [{ mouseButton: MouseButton.PRIMARY }],
            });
            group.setToolActive(ZoomTool.toolName, {
                bindings: [{ mouseButton: MouseButton.SECONDARY }],
            });
            group.setToolActive(PanTool.toolName, {
                bindings: [{ mouseButton: MouseButton.AUXILIARY }],
            });
            group.setToolActive(VerticalRotateTool.toolName, {
                bindings: [{ mouseButton: MouseButton.WHEEL }],
            });
            group.setToolActive(CurrentLocationTool.toolName, {
                bindings: [{ mouseButton: MouseButton.PRIMARY }],
            });
        }
        if (isSlice) {
            // 2D Slice viewports (axial, sagittal, coronal, oblique)
            group.addTool(ContrastTool.toolName);
            group.addTool(PanTool.toolName);
            group.addTool(ZoomTool.toolName);
            group.addTool(SliceScrollTool.toolName);
            group.addTool(CurrentLocationTool.toolName);
            group.addTool(DistanceMeasurementTool.toolName);
            group.addTool(DiameterEditTool.toolName);

            group.setToolActive(ContrastTool.toolName, {
                bindings: [{ mouseButton: MouseButton.PRIMARY }],
            });
            group.setToolActive(PanTool.toolName, {
                bindings: [{ mouseButton: MouseButton.AUXILIARY }],
            });
            group.setToolActive(ZoomTool.toolName, {
                bindings: [{ mouseButton: MouseButton.SECONDARY }],
            });
            group.setToolActive(SliceScrollTool.toolName, {
                bindings: [{ mouseButton: MouseButton.WHEEL }],
            });
            group.setToolActive(CurrentLocationTool.toolName, {
                bindings: [{ mouseButton: MouseButton.PRIMARY }],
            });

            group.setToolPassive(DiameterEditTool.toolName);
        }
        if (isCPR) {
            // CPR viewports (stretched, straightened)
            // No Zoom/Pan: CPR is always fit to viewport height for correct overlay positioning
            group.addTool(ContrastTool.toolName);
            group.addTool(AngleScrollTool.toolName);
            group.addTool(CurrentLocationTool.toolName);

            group.setToolActive(ContrastTool.toolName, {
                bindings: [{ mouseButton: MouseButton.PRIMARY }],
            });
            group.setToolActive(AngleScrollTool.toolName, {
                bindings: [{ mouseButton: MouseButton.WHEEL }],
            });
            group.setToolActive(CurrentLocationTool.toolName, {
                bindings: [{ mouseButton: MouseButton.PRIMARY }],
            });
        }
    }

    // ── Lifecycle ──

    override reset(): void {
        for (const group of this.toolGroups.values()) {
            group.dispose();
        }
        this.toolGroups.clear();
        this.viewportToGroupId.clear();
        super.reset();
    }
}
